import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ref, get } from "firebase/database";
import { database } from "./adm/admin/firebaseConfig";

import {
  Container,
  Typography,
  Box,
  Card,
  Grid,
  Avatar,
  Skeleton,
  Button,
  Divider,
  Stack,
} from "@mui/material";

import {
  Language, Facebook, Instagram, YouTube, LinkedIn,
  WhatsApp, Email, Phone, Twitter, Public, Link as LinkIcon,
  School, Business, Security, Nature, Agriculture, DirectionsCar,
  ArrowForwardIos,
} from "@mui/icons-material";

const iconMap = {
  language: <Language />, facebook: <Facebook />, instagram: <Instagram />,
  youtube: <YouTube />, linkedin: <LinkedIn />, twitter: <Twitter />,
  whatsapp: <WhatsApp />, email: <Email />, phone: <Phone />,
  public: <Public />, link: <LinkIcon />, school: <School />,
  business: <Business />, security: <Security />, nature: <Nature />,
  agriculture: <Agriculture />, directions_car: <DirectionsCar />,
};

const SecretaryPage = ({ nome, descricao, cargo, email, telefone, imagem, links = [] }) => {
  const validLinks = links.filter((link) => link?.nome?.trim() && link?.url?.trim());

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: { xs: 2, md: 6 } }}>
      <Card
        sx={{
          bgcolor: "#F8F9FA",
          borderRadius: 6,
          border: "1px solid #E0E0E0",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Grid container>
          {/* LADO ESQUERDO: PERFIL */}
          <Grid item xs={12} md={5} sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            bgcolor: "#FFFFFF",
            borderRight: { md: "1px solid #EDEDED" }
          }}>
            <Avatar
              src={imagem}
              alt={nome}
              sx={{
                width: 180,
                height: 180,
                mb: 3,
                border: "4px solid #FFF",
                boxShadow: "0 8px 24px rgba(9, 30, 58, 0.25)",
              }}
            />
            <Typography variant="h4" fontWeight="800" color="#1A1A1A" sx={{ mb: 1 }}>
              {nome}
            </Typography>
            <Typography variant="h6" color="#33383fff" fontWeight="600" sx={{ mb: 3 }}>
              {cargo}
            </Typography>

            <Stack spacing={1} sx={{ width: "100%", mt: "auto" }}>
              {email && (
                <Typography variant="body2" color="#666" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Email fontSize="small" color="primary" /> {email}
                </Typography>
              )}
              {telefone && (
                <Typography variant="body2" color="#666" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Phone fontSize="small" color="primary" /> {telefone}
                </Typography>
              )}
            </Stack>
          </Grid>

          {/* LADO DIREITO: CONTEÚDO E LINKS */}
          <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 6 } }}>
            <Box mb={4}>
              <Typography variant="overline" color="#0F71FD" fontWeight="bold" sx={{ letterSpacing: 1.1 }}>
                Informações Gerais
              </Typography>
              <Typography variant="body1" color="#444" sx={{ mt: 1, lineHeight: 1.7, fontSize: "1.05rem" }}>
                {descricao}
              </Typography>
            </Box>

            {/* RENDERIZAÇÃO CONDICIONAL DOS LINKS */}
            {validLinks.length > 0 && (
              <>
                <Divider sx={{ mb: 4 }} />

                <Typography variant="subtitle1" color="#1A1A1A" fontWeight="700" mb={2.5}>
                  Links disponibilizados
                </Typography>

                <Stack spacing={2}>
                  {validLinks.map((link, index) => (
                    <Button
                      key={index}
                      fullWidth
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<ArrowForwardIos sx={{ fontSize: 14 }} />}
                      sx={{
                        justifyContent: "space-between",
                        p: 2,
                        borderRadius: 3,
                        bgcolor: "#FFF",
                        color: "#333",
                        border: "1px solid #EBEBEB",
                        textTransform: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "#0F71FD",
                          color: "#FFF",
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 20px rgba(15, 113, 253, 0.2)",
                          border: "1px solid #0F71FD",
                          "& .MuiAvatar-root": { bgcolor: "rgba(255, 255, 255, 0.2)", color: "#FFF" }
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: "#F0F4F8",
                            color: "#0F71FD",
                            width: 38,
                            height: 38,
                          }}
                        >
                          {iconMap[link.icone] || <LinkIcon />}
                        </Avatar>
                        <Typography fontWeight="600">{link.nome}</Typography>
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </>
            )}
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default function ViewSecretario() {
  const [secretario, setSecretario] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();

  const fetchSecretario = useCallback(async () => {
    try {
      const secretarioRef = ref(database, `secretarios/${id}`);
      const snapshot = await get(secretarioRef);
      if (snapshot.exists()) setSecretario(snapshot.val());
    } catch (error) {
      console.error("Erro:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSecretario(); }, [fetchSecretario]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={500} sx={{ borderRadius: 6 }} />
      ) : secretario ? (
        <SecretaryPage {...secretario} />
      ) : (
        <Typography align="center">Secretário não encontrado.</Typography>
      )}
    </Container>
  );
}